import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Badge, Button, Card } from '../../ui';
import {
  getMyClientRow,
  getMyInstance,
  getSession,
  listThreadMessages,
} from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatMoment, formatTime } from '../../shared/formatDate';
import { useInstance } from '../instance/InstanceProvider';
import { text } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientPage } from './ClientPage';
import { markThreadRead, sendMessage, unreadMessages, usePresence } from './presence';
import { hasConsent, useChoices } from './consentChoices';

/**
 * The one thread between a client and the person coaching them.
 *
 * ## There is no hi-fi for this screen, and this is what was done instead
 *
 * The design project has exactly three screen files — auth, train and account —
 * and **none of them is a thread**. So unlike every other client screen in this
 * product, this one has no mock to follow and was built from the layer below:
 * the design tokens and the components already ported from them. Concretely:
 *
 *   * a message is a `Card`. The coach's are the raised white card the product
 *     uses for content; the client's are the tinted `soft` card the product
 *     already uses for "the thing that is yours" — today's session, the
 *     confirmation. Both grounds and both ink weights are **already in the
 *     contrast table** in `src/index.css` (`ink` at 18.84:1 and 15.26:1), so the
 *     screen introduces no new colour pairing and the audit needed no new line;
 *   * **no bubble tails, no avatars and no chat-app furniture.** A thread that
 *     looks like a messaging app will be used like one, and this one has to read
 *     as correspondence with a person who answers within a day. The visual
 *     difference between the two sides is the ground and the label above it,
 *     which is also what a screen reader gets;
 *   * every message says **who wrote it and when**, above it, in words — the
 *     same rule every other surface in this product follows;
 *   * the composer is a `textarea` and a `Button`, which is the whole of it.
 *
 * ## Three refusals, all load-bearing
 *
 *   * **Text only.** There is no attachment control anywhere on this screen, and
 *     the absence *is* the enforcement: `mvp-discovery` records attachments as
 *     the way a health file returns through the back door.
 *   * **Not an emergency channel**, said inside the thread, with the coach's own
 *     reply expectation beside it so nobody reads this as a line that is
 *     watched.
 *   * **The product never asks about pain.** A client may write whatever they
 *     choose; nothing here asks where, how much, or for how long, and a message
 *     opened from a check-in carries a *reference to the session* and no text.
 *
 * ## What is hollow here, said out loud
 *
 * **There is no `message` table and there is no coach's side.** What the client
 * writes is kept on this device by `./presence.ts` and reaches nobody; the
 * messages from the coach in a demonstration come from the fixtures, and in a
 * real build there are none. The screen says so, permanently, under the
 * composer — because a composer that accepted a message and implied it had been
 * delivered would be the single most dishonest thing this product could do, and
 * this is the screen a client would use to say that something hurts.
 *
 * That is why gate item **B10** in `docs/runbooks/before-the-first-real-client.md`
 * exists: this path must reach a real person before any real client uses the
 * product.
 */
export function MessagesPage() {
  return (
    <ClientGuard>
      <Messages />
    </ClientGuard>
  );
}

function Messages() {
  const instance = useInstance();
  const [params, setParams] = useSearchParams();

  /*
   * The session a check-in sent the client here from. A reference, carried in
   * the address so that a reload keeps it, and removable before sending.
   */
  const referenceId = params.get('re');
  const [reference, setReference] = useState<string | null>(referenceId);

  /*
   * `my-instance` is the key `ClientGuard` already warmed, so this is a cache
   * hit rather than a second request. It carries `reply_expectation`, which is
   * the coach's own sentence and never a literal in this file.
   */
  const mine = useQuery({ queryKey: ['my-instance'], queryFn: () => getMyInstance(supabase) });
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });
  const thread = useQuery({
    queryKey: ['thread-messages'],
    queryFn: () => listThreadMessages(supabase),
  });

  const session = useQuery({
    queryKey: ['session', reference ?? ''],
    queryFn: () => getSession(supabase, reference ?? ''),
    enabled: reference !== null,
  });

  const presence = usePresence();
  /*
   * `client-me` made the consent something the client can change, and a client
   * cannot write their own `client` row — there is no `client_update_self`
   * policy — so the current answer is the row overlaid by this device's choice.
   * Reading the column alone would mean turning messaging off on the profile
   * screen and still being shown the thread.
   */
  const choices = useChoices();

  /*
   * What was unread *on arrival*. Opening the thread clears it, so the value
   * has to be captured before that happens or the markers this visit is meant
   * to show would be gone before the first paint.
   */
  const [readBefore] = useState<string | null>(() => presence.threadReadAt);

  const messages = thread.data;
  useEffect(() => {
    if (messages !== undefined) markThreadRead();
  }, [messages]);

  const [draft, setDraft] = useState('');

  if (me.isPending || mine.isPending) return <ClientLoading />;

  /*
   * Messaging follows the consent and its absence is explained rather than
   * hidden. Not a disabled composer, not an empty thread, not a nag somewhere
   * else in the product: one screen, one explanation, one way to change it.
   */
  if (!hasConsent('messaging', me.data, choices)) {
    return <MessagingOff slug={instance.slug} coach={instance.coachDisplayName} />;
  }

  const unread = new Set(unreadMessages(messages ?? [], readBefore).map((message) => message.id));

  const lines = [
    ...(messages ?? []).map((message) => ({
      id: message.id,
      author: message.author,
      body: message.body,
      at: message.sent_at,
      unread: unread.has(message.id),
    })),
    ...presence.sent.map((message) => ({
      id: message.id,
      author: 'client' as const,
      body: message.body,
      at: message.at,
      unread: false,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  const submit = () => {
    const body = draft.trim();
    if (body === '') return;
    sendMessage(body, reference);
    setDraft('');
    setReference(null);
    setParams({}, { replace: true });
  };

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/today`} backLabel={text('client.messages.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.messages.title')}
      </h1>

      {/*
        What this thread is and is not, visible inside it rather than shown once
        and forgotten. Her role line comes from the instance row: she is a
        personal trainer who is also a medical student, and no screen in this
        product may describe her otherwise.

        The emergency line does not name a number. There is no emergency-number
        setting on `coach_instance`, this change adds no migration, and a
        hard-coded "112" is right in Spain and wrong for a client on holiday.
      */}
      <Card className="mt-4" role="note">
        <p className="text-ink-soft text-sm leading-relaxed">
          {text('client.messages.notice', {
            coach: instance.coachDisplayName,
            role: instance.roleLine,
          })}
        </p>
        <p className="text-ink mt-2 text-sm leading-relaxed">{text('client.messages.emergency')}</p>
        {/*
          Her own words about when she answers, rendered exactly as she typed
          them. Where she has set none, nothing is shown — an invented promise
          about her availability is not ours to make.
        */}
        {mine.data?.reply_expectation == null ? null : (
          <p className="text-ink-muted mt-2 text-sm">{mine.data.reply_expectation}</p>
        )}
      </Card>

      {thread.isPending ? (
        <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p>
      ) : null}

      {!thread.isPending && lines.length === 0 ? (
        <p className="text-ink-soft mt-4 text-sm">{text('client.messages.emptyThread')}</p>
      ) : null}

      {lines.length === 0 ? null : (
        <ol className="mt-4 flex flex-col gap-3">
          {lines.map((line) => (
            <li
              key={line.id}
              className={line.author === 'client' ? 'ml-6 sm:ml-12' : 'mr-6 sm:mr-12'}
            >
              <p className="text-ink-muted flex flex-wrap items-center gap-2 text-xs">
                <span>
                  {line.author === 'coach'
                    ? text('client.messages.fromCoach', { coach: instance.coachDisplayName })
                    : text('client.messages.fromMe')}
                </span>
                <span>{`${formatMoment(line.at)} · ${formatTime(line.at)}`}</span>
                {line.unread ? (
                  <Badge tone="attention">{text('client.messages.unread')}</Badge>
                ) : null}
              </p>
              <Card tone={line.author === 'client' ? 'soft' : 'raised'} className="mt-1">
                <p className="text-ink text-sm leading-relaxed whitespace-pre-line">{line.body}</p>
              </Card>
            </li>
          ))}
        </ol>
      )}

      {/* --- the composer: text, and nothing else ----------------------- */}
      <div className="mt-6 flex flex-col gap-1.5">
        {/*
          The reference the check-in sent, if any. It names the session and
          nothing about what happened in it, it prefills no text, and it can be
          taken off before sending.
        */}
        {reference === null || !session.data ? null : (
          <p className="flex flex-wrap items-center gap-2">
            <span className="bg-surface-muted text-ink-soft inline-flex items-center rounded-full px-3 py-1 text-xs">
              {text('client.messages.re', { title: session.data.title })}
            </span>
            <button
              type="button"
              aria-label={text('client.messages.reRemove')}
              onClick={() => {
                setReference(null);
                setParams({}, { replace: true });
              }}
              className="text-ink-muted min-h-11 text-xs underline underline-offset-2"
            >
              {text('client.messages.reRemove')}
            </button>
          </p>
        )}

        <label htmlFor="message-body" className="text-ink text-sm font-medium">
          {text('client.messages.composerLabel')}
        </label>
        <textarea
          id="message-body"
          value={draft}
          maxLength={1000}
          rows={3}
          placeholder={text('client.messages.composerPlaceholder')}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          className="bg-surface-raised text-ink border-border-strong rounded-[--radius-control] border px-3 py-2 text-base"
        />
        <div>
          <Button variant="primary" onClick={submit}>
            {text('client.messages.send')}
          </Button>
        </div>

        {/*
          The truth about this screen, under the control that would otherwise
          imply the opposite. It is not a footnote and it is not dismissible.
        */}
        <p className="text-ink-muted mt-1 text-xs leading-relaxed">
          {text('client.messages.notDelivered', { coach: instance.coachDisplayName })}
        </p>
      </div>
    </ClientPage>
  );
}

/**
 * The client who has not opted in to stored messages.
 *
 * No thread, no composer, no history — and an explanation rather than a
 * disabled tab, because a client who cannot find messaging assumes it is broken
 * (`client-coach-presence` design, D4). It offers the choice once, here, and
 * nothing anywhere else in the product repeats the request or blocks on it.
 *
 * **Where to turn it on is stated honestly.** It lives on the You screen, which
 * is slice 4 and does not exist yet, so this says that too rather than sending
 * a client to a screen that is not there.
 */
function MessagingOff({ slug, coach }: { slug: string; coach: string }) {
  return (
    <ClientPage>
      <AppBar backTo={`/c/${slug}/today`} backLabel={text('client.messages.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.messages.title')}
      </h1>

      <Card className="mt-4">
        <h2 className="text-ink font-display text-xl">{text('client.messages.offTitle')}</h2>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.messages.offBody')}
        </p>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.messages.offHow', { coach })}
        </p>
      </Card>

      <Link
        to={`/c/${slug}/today`}
        className="text-primary mt-6 inline-flex min-h-11 items-center text-sm font-medium underline-offset-2 hover:underline"
      >
        {text('client.messages.back')}
      </Link>
    </ClientPage>
  );
}
